<template>
  <nav
    class="navbar"
    :class="{ 'navbar--scrolled': scrolled, 'navbar--open': menuOpen }"
  >
    <div class="navbar__inner">
      <router-link
        to="/"
        class="navbar__logo"
        @click="menuOpen = false"
      >
        E <span class="navbar__ampersand">&</span> A
      </router-link>

      <button
        class="navbar__toggle"
        :aria-label="menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'"
        @click="menuOpen = !menuOpen"
      >
        <span
          class="navbar__toggle-line"
          :class="{ 'navbar__toggle-line--open': menuOpen }"
        />
        <span
          class="navbar__toggle-line"
          :class="{ 'navbar__toggle-line--open': menuOpen }"
        />
        <span
          class="navbar__toggle-line"
          :class="{ 'navbar__toggle-line--open': menuOpen }"
        />
      </button>

      <ul
        class="navbar__links"
        :class="{ 'navbar__links--open': menuOpen }"
      >
        <li
          v-for="link in links"
          :key="link.to"
        >
          <router-link
            :to="link.to"
            class="navbar__link"
            active-class="navbar__link--active"
            @click="menuOpen = false"
          >
            {{ link.label }}
          </router-link>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue"

const scrolled = ref(false)
const menuOpen = ref(false)

const links = [
  { to: "/", label: "Accueil" },
  { to: "/lieu", label: "Le lieu" },
  { to: "/programme", label: "Programme" },
  { to: "/dresscode", label: "Dress code" },
  { to: "/logement", label: "Logement" },
  { to: "/invites", label: "Invités" },
  { to: "/liste-de-mariage", label: "Liste" },
  { to: "/faq", label: "FAQ" },
]

function onScroll() {
  scrolled.value = window.scrollY > 40
}

onMounted(() => window.addEventListener("scroll", onScroll))
onUnmounted(() => window.removeEventListener("scroll", onScroll))
</script>

<style lang="scss" scoped>
@import "../base/mixins";

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: transparent;
  transition: background-color 0.3s, box-shadow 0.3s;
  isolation: isolate;

  /* default (top of page) foreground */
  --nav-fg: #fff;
  --nav-shadow: 0 2px 12px rgba(0, 0, 0, 0.45);
}

/* when scrolled/open -> go back to design system colors */
.navbar--scrolled,
.navbar--open {
  --nav-fg: #{$color-text-primary};
  --nav-shadow: none;
}

.navbar__inner {
  position: relative;
  z-index: 1;
}

/* apply foreground everywhere */
.navbar__logo,
.navbar__link {
  color: var(--nav-fg);
  text-shadow: var(--nav-shadow);
}

/* burger lines */
.navbar__toggle-line {
  background-color: var(--nav-fg);
  box-shadow: var(--nav-shadow);
}

/* overlay behind the navbar (this makes it readable on any photo) */
.navbar::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.3s;

  background: linear-gradient(
    to bottom,
    rgba($color-bg-primary, 0.90) 0%,
    rgba($color-bg-primary, 0.65) 55%,
    rgba($color-bg-primary, 0.40) 75%,
    rgba($color-bg-primary, 0) 100%
  );
}

/* hide overlays when scrolled/open (since you already have solid bg then) */
.navbar--scrolled::before,
.navbar--scrolled::after,
.navbar--open::before,
.navbar--open::after {
  opacity: 0;
}

.navbar--scrolled {
  background-color: rgba($color-bg-primary, 0.95);
  backdrop-filter: blur(8px);
  box-shadow: 0 1px 0 $color-border;
}

.navbar--open {
  background-color: rgba($color-bg-primary, 0.98);
}

.navbar__inner {
  @include container;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-element $spacing-section-x;

  @media (min-width: $bp-tablet) {
    padding: 20px $spacing-section-x-desktop;
  }
}

.navbar__logo {
  @include heading;
  font-size: 1.5rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-decoration: none;
}

.navbar__ampersand {
  font-style: italic;
  font-weight: 300;
  margin: 0 2px;
}

.navbar__toggle {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: $spacing-element-sm;

  @media (min-width: $bp-tablet) {
    display: none;
  }
}

.navbar__toggle-line {
  display: block;
  width: 24px;
  height: 1.5px;
  background-color: $color-text-primary;
  transition: transform 0.3s, opacity 0.3s;

  &--open:first-child {
    transform: translateY(7.3px) rotate(45deg);
  }

  &--open:nth-child(2) {
    opacity: 0;
  }

  &--open:last-child {
    transform: translateY(-7.3px) rotate(-45deg);
  }
}

.navbar__links {
  list-style: none;
  display: none;
  flex-direction: column;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: rgba($color-bg-primary, 0.98);
  padding: $spacing-element $spacing-section-x $spacing-block;

  &--open {
    display: flex;
  }

  @media (min-width: $bp-tablet) {
    display: flex;
    flex-direction: row;
    position: static;
    background: none;
    padding: 0;
    gap: 30px;
  }
}

.navbar__link {
  @include body-text;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 10px 0;
  display: block;
  transition: color 0.2s;

  &:hover {
    color: $color-accent;
  }

  &--active {
    color: $color-accent;
  }

  @media (min-width: $bp-tablet) {
    padding: 0;
    font-size: 0.75rem;
  }
}

.navbar:not(.navbar--scrolled) .navbar__link {
  &:hover {
    color: $nav-accent-top;
  }

  &--active {
    color: $nav-accent-top;
    font-weight: 500;
  }
}
</style>
